import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl, TRAINER_COLOR_PRESETS } from "../../../utils/clientHeroTheme";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import { Avatar, Skeleton, useToast } from "../../ui";
import s from "./clientApp.module.css";

function CameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7a3 3 0 0 1 3-3h2.2L10 2h4l.8 2H17a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7Z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <circle cx="12" cy="12" r="3.25" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function SettingSwitch({ label, hint, on, onChange }) {
  return (
    <div className={s.socialSwitchRow}>
      <div className={s.socialSwitchText}>
        <strong>{label}</strong>
        <span>{hint}</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className={on ? s.socialSwitchTrackOn : s.socialSwitchTrack}
        onClick={() => onChange(!on)}
      >
        <span className={s.socialSwitchKnob} />
      </button>
    </div>
  );
}

export default function ClientSocialSettings() {
  const toast = useToast();
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [profilePublic, setProfilePublic] = useState(true);
  const [showInDirectory, setShowInDirectory] = useState(true);
  const [theme, setTheme] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const coverInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await clinicAPI.getMySocialProfile();
      const p = data.profile || {};
      setProfile(p);
      setBio(p.profile_bio || "");
      setProfilePublic(p.profile_public !== false);
      setShowInDirectory(p.show_in_directory !== false);
      setTheme(p.profile_theme || "");
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useClientRefresh(load);

  const save = async () => {
    setSaving(true);
    try {
      const data = await clinicAPI.updateMySocialProfile({
        profile_bio: bio,
        profile_public: profilePublic,
        show_in_directory: showInDirectory,
        profile_theme: theme || null,
      });
      setProfile(data.profile);
      toast.success("Профиль обновлён");
    } catch {
      toast.error("Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await clinicAPI.uploadSocialCover(file);
      setProfile((prev) =>
        prev ? { ...prev, profile_cover_url: r.url } : prev,
      );
      toast.success("Обложка обновлена");
    } catch {
      toast.error("Не удалось загрузить обложку");
    }
    e.target.value = "";
  };

  if (loading) {
    return (
      <div className={s.socialEditPage}>
        <Skeleton height={140} style={{ borderRadius: 16 }} />
        <Skeleton height={220} style={{ borderRadius: 16, marginTop: 12 }} />
      </div>
    );
  }

  const coverUrl = mediaUrl(profile?.profile_cover_url);
  const activeTheme = TRAINER_COLOR_PRESETS.find((p) => p.id === theme);

  return (
    <div className={s.socialEditPage}>
      <section className={s.socialEditCoverWrap} aria-label="Обложка профиля">
        <div
          className={s.socialEditCover}
          style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
        >
          <div className={s.socialEditCoverFade} aria-hidden />
          <button
            type="button"
            className={s.socialEditCoverBtn}
            onClick={() => coverInputRef.current?.click()}
          >
            <CameraIcon />
            <span>{coverUrl ? "Изменить обложку" : "Добавить обложку"}</span>
          </button>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={uploadCover}
          />
        </div>

        <div className={s.socialEditIdentity}>
          <Avatar
            name={profile?.full_name}
            src={mediaUrl(profile?.photo_url)}
            size="lg"
            className={s.socialEditAvatar}
          />
          <div className={s.socialEditIdentityText}>
            <p className={s.socialEditName}>{profile?.full_name || "Профиль"}</p>
            <Link
              to={`/app/members/${profile?.id}`}
              className={s.socialEditPreviewLink}
            >
              <EyeIcon />
              Как видят другие
            </Link>
          </div>
        </div>
      </section>

      <div className={s.socialEditCard}>
        <section className={s.socialEditBlock}>
          <h3 className={s.socialEditBlockTitle}>О себе</h3>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="Расскажите о себе, целях, увлечениях…"
            className={s.socialEditBio}
          />
          <p className={s.socialEditCounter}>{bio.length} / 2000</p>
        </section>

        <section className={s.socialEditBlock}>
          <h3 className={s.socialEditBlockTitle}>Приватность</h3>
          <div className={s.socialSwitchList}>
            <SettingSwitch
              label="Публичный профиль"
              hint="Другие участники клуба смогут видеть вашу страницу"
              on={profilePublic}
              onChange={setProfilePublic}
            />
            <SettingSwitch
              label="Показывать в каталоге"
              hint="Отображаться в списке участников группы"
              on={showInDirectory}
              onChange={setShowInDirectory}
            />
          </div>
        </section>

        <section className={s.socialEditBlock}>
          <h3 className={s.socialEditBlockTitle}>Цвет профиля</h3>
          <p className={s.socialEditBlockHint}>
            Акцент на вашей странице и в каталоге участников
          </p>
          <div className={s.socialThemePicker}>
            <button
              type="button"
              className={!theme ? s.socialThemeChipOn : s.socialThemeChip}
              onClick={() => setTheme("")}
            >
              <span className={s.socialThemeChipDotDefault} />
              <span>По умолчанию</span>
            </button>
            {TRAINER_COLOR_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={theme === p.id ? s.socialThemeChipOn : s.socialThemeChip}
                onClick={() => setTheme(p.id)}
              >
                <span
                  className={s.socialThemeChipDot}
                  style={{ background: p.color }}
                />
                <span>{p.label}</span>
              </button>
            ))}
          </div>
          {activeTheme ? (
            <p
              className={s.socialThemePreview}
              style={{ color: activeTheme.color }}
            >
              Выбран: {activeTheme.label}
            </p>
          ) : null}
        </section>
      </div>

      <div className={s.socialEditFooter}>
        <button
          type="button"
          className={s.socialEditSaveBtn}
          onClick={save}
          disabled={saving}
        >
          {saving ? "Сохранение…" : "Сохранить изменения"}
        </button>
      </div>
    </div>
  );
}
